// src/pages/finance-portal/payroll-runs/PayrollRuns.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment, MenuItem, Select, FormControl, InputLabel,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    CALCULATED: { bg: '#dbeafe', color: '#1e40af' },
    APPROVED: { bg: '#e0e7ff', color: '#3730a3' },
    COMPLETED: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Calculated', value: 'CALCULATED' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Void', value: 'VOID' },
];

const PAY_FREQUENCY_OPTIONS = [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'BIWEEKLY', label: 'Bi-Weekly' },
    { value: 'SEMIMONTHLY', label: 'Semi-Monthly' },
    { value: 'MONTHLY', label: 'Monthly' },
];

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const defaultNewRun = {
    pay_frequency: 'BIWEEKLY',
    period_start: '',
    period_end: '',
    pay_date: '',
    memo: '',
};

export default function PayrollRuns() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [runs, setRuns] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');

    // New run dialog
    const [newRunDialog, setNewRunDialog] = useState(false);
    const [newRunForm, setNewRunForm] = useState({ ...defaultNewRun });
    const [creating, setCreating] = useState(false);

    // Action states
    const [actionLoading, setActionLoading] = useState({});

    // Delete / Void dialogs
    const [deleteDialog, setDeleteDialog] = useState({ open: false, run: null });
    const [deleting, setDeleting] = useState(false);
    const [voidDialog, setVoidDialog] = useState({ open: false, run: null });
    const [voiding, setVoiding] = useState(false);

    const fetchRuns = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;

            const res = await axiosInstance.get('/api/finance/payroll/runs', { params });
            if (res.data.success) {
                setRuns(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payroll runs');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter]);

    useEffect(() => { fetchRuns(); }, [fetchRuns]);

    const handleCreateRun = async () => {
        setCreating(true);
        try {
            const res = await axiosInstance.post('/api/finance/payroll/runs', newRunForm);
            if (res.data.success) {
                enqueueSnackbar('Payroll run created.', { variant: 'success' });
                setNewRunDialog(false);
                setNewRunForm({ ...defaultNewRun });
                fetchRuns();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to create payroll run.', { variant: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const handleCalculate = async (runId) => {
        setActionLoading(prev => ({ ...prev, [runId]: 'calculate' }));
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${runId}/calculate`);
            enqueueSnackbar('Payroll calculated successfully.', { variant: 'success' });
            fetchRuns();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to calculate payroll.', { variant: 'error' });
        } finally {
            setActionLoading(prev => ({ ...prev, [runId]: null }));
        }
    };

    const handleApprove = async (runId) => {
        setActionLoading(prev => ({ ...prev, [runId]: 'approve' }));
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${runId}/approve`);
            enqueueSnackbar('Payroll approved.', { variant: 'success' });
            fetchRuns();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to approve payroll.', { variant: 'error' });
        } finally {
            setActionLoading(prev => ({ ...prev, [runId]: null }));
        }
    };

    const handleVoid = async () => {
        setVoiding(true);
        try {
            await axiosInstance.post(`/api/finance/payroll/runs/${voidDialog.run.id}/void`);
            enqueueSnackbar('Payroll run voided.', { variant: 'success' });
            setVoidDialog({ open: false, run: null });
            fetchRuns();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to void payroll run.', { variant: 'error' });
        } finally {
            setVoiding(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/payroll/runs/${deleteDialog.run.id}`);
            enqueueSnackbar('Payroll run deleted.', { variant: 'success' });
            setDeleteDialog({ open: false, run: null });
            fetchRuns();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete payroll run.', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Payroll Runs</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Process and manage payroll cycles</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setNewRunDialog(true)}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        New Payroll Run
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx].value); setPage(0); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, textTransform: 'none', fontWeight: 600,
                            fontSize: '0.85rem', color: '#64748b', minHeight: 40,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Run #', 'Frequency', 'Period', 'Pay Date', 'Employees', 'Gross Pay', 'Net Pay', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : runs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                                            <AccountBalanceIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No payroll runs found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => setNewRunDialog(true)}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Create your first payroll run
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : runs.map(run => {
                                    const statusStyle = STATUS_COLORS[run.status] || {};
                                    const isActionLoading = actionLoading[run.id];
                                    return (
                                        <TableRow
                                            key={run.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                            onClick={() => navigate(`/finance-portal/payroll-runs/${run.id}`)}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {run.run_number || '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {getStatusLabel(run.pay_frequency || '')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(run.period_start)} - {formatDate(run.period_end)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(run.pay_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textAlign: 'center' }}>
                                                {run.employee_count ?? run.employees_count ?? 0}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(run.total_gross)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(run.total_net)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(run.status)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell onClick={e => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View Detail">
                                                        <IconButton size="small" onClick={() => navigate(`/finance-portal/payroll-runs/${run.id}`)} sx={{ color: '#94a3b8' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {(run.status === 'DRAFT') && (
                                                        <Tooltip title="Calculate">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCalculate(run.id)}
                                                                disabled={!!isActionLoading}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
                                                            >
                                                                {isActionLoading === 'calculate' ? <CircularProgress size={16} /> : <CalculateIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(run.status === 'CALCULATED') && (
                                                        <Tooltip title="Approve">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleApprove(run.id)}
                                                                disabled={!!isActionLoading}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}
                                                            >
                                                                {isActionLoading === 'approve' ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {run.status !== 'VOID' && run.status !== 'COMPLETED' && (
                                                        <Tooltip title="Void">
                                                            <IconButton size="small" onClick={() => setVoidDialog({ open: true, run })} sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}>
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {run.status === 'DRAFT' && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, run })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* New Payroll Run Dialog */}
            <Dialog open={newRunDialog} onClose={() => setNewRunDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>New Payroll Run</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <FormControl size="small" sx={inputSx}>
                            <InputLabel sx={{ fontFamily }}>Pay Frequency</InputLabel>
                            <Select
                                value={newRunForm.pay_frequency}
                                label="Pay Frequency"
                                onChange={e => setNewRunForm(prev => ({ ...prev, pay_frequency: e.target.value }))}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {PAY_FREQUENCY_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Period Start"
                            size="small"
                            type="date"
                            value={newRunForm.period_start}
                            onChange={e => setNewRunForm(prev => ({ ...prev, period_start: e.target.value }))}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            sx={inputSx}
                            required
                        />
                        <TextField
                            label="Period End"
                            size="small"
                            type="date"
                            value={newRunForm.period_end}
                            onChange={e => setNewRunForm(prev => ({ ...prev, period_end: e.target.value }))}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            sx={inputSx}
                            required
                        />
                        <TextField
                            label="Pay Date"
                            size="small"
                            type="date"
                            value={newRunForm.pay_date}
                            onChange={e => setNewRunForm(prev => ({ ...prev, pay_date: e.target.value }))}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            sx={inputSx}
                            required
                        />
                        <TextField
                            label="Memo (optional)"
                            size="small"
                            value={newRunForm.memo}
                            onChange={e => setNewRunForm(prev => ({ ...prev, memo: e.target.value }))}
                            multiline
                            rows={2}
                            sx={inputSx}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNewRunDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateRun}
                        disabled={creating || !newRunForm.period_start || !newRunForm.period_end || !newRunForm.pay_date}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                        }}
                    >
                        {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Run'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Void Confirmation Dialog */}
            <Dialog open={voidDialog.open} onClose={() => setVoidDialog({ open: false, run: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Void Payroll Run {voidDialog.run?.run_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will void the payroll run. This action cannot be undone. All associated pay stubs will also be voided.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVoidDialog({ open: false, run: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVoid}
                        disabled={voiding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {voiding ? 'Voiding...' : 'Void Run'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, run: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Payroll Run {deleteDialog.run?.run_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete this draft payroll run. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, run: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
